// AdminPage.js
import React, { useState, useEffect } from 'react';
import { colors } from '../styles/designSystem';
import { Container } from './ui/Container';
import { GradientButton } from './ui/GradientButton';
import log from '../logger';
import { Plus, Edit2, Eye, X, ShieldCheck, Settings } from 'lucide-react';
import { 
    getSectors, 
    //getTemplatesBySector, 
    getSectorTemplates,
    generateTemplate, 
    updateTemplate 
} from '../api';

// Import the EnvChecker component
import EnvChecker from './EnvChecker'; // Adjust the path based on your project structure

const AdminPage = () => {
    // Existing state management
    const [sectors, setSectors] = useState([]);
    const [selectedSector, setSelectedSector] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState({ template: '' });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        log.debug('AdminPage mounted');

        // Use getSectors API function
        getSectors()
                .then(data => {
                    log.debug('Fetched sectors:', data);
                    setSectors(data);
                })
                .catch(error => {
                    log.error('Error fetching sectors:', error);
                    setError('Failed to load sectors. Please try again later.');
                });

    }, []);

    const handleViewTemplates = async (sector) => {
        setSelectedSector(sector);
        setLoading(true);
        try {
            log.debug('Fetched handleViewTemplates:');

            // Correct: Directly using the returned data
            const data = await getSectorTemplates(sector._id);
            log.debug('Templates data:', data);
            setTemplates(data);
        } catch (error) {
            log.error('Error fetching templates:', error);
            setError('Failed to load templates for the selected sector.');
        }
        setLoading(false);
    };
    

    const handleEditTemplate = (template) => {
        setCurrentTemplate(template);
        setShowModal(true);
    };

    const handleTemplateChange = (e) => {
        setCurrentTemplate({
            ...currentTemplate,
            template: e.target.value, // **UPDATED: Handle single 'template' string**
        });
    };

    const handleSaveTemplate = async () => {
        setLoading(true);
        setError('');
        try {
            // Use updateTemplate API function
            log.debug('Fetched handleSaveTemplate:');

            const response = await updateTemplate(currentTemplate._id, currentTemplate.template);
            setMessage(response.data.message);
            // Refresh templates
            handleViewTemplates(selectedSector);
            setShowModal(false);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            log.error('Error saving template:', error);
            setError('Failed to save the template.');
        }
        setLoading(false);
    };

    const handleGenerateTemplate = async (sector) => {
        setGenerating(true);
        setError('');
        try {
            log.debug('Fetched handleGenerateTemplate:');

            // Use generateTemplate API function
            const response = await generateTemplate(sector._id);
            setMessage(response.data.message);
            // Refresh templates
            handleViewTemplates(sector);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            log.error('Error generating template:', error);
            setError('Failed to generate FAQ template.');
        }
        setGenerating(false);
    };

    return (
        <section className="py-6 relative overflow-hidden">
            {/* Background with gradient - matching source */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-100" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                {/* Header - matching source KeyFeatures style */}
                <div className="text-center mb-16">
                    <h2 className={`${colors.text.heading} ${colors.primary.text} mb-6`}>
                        **********FAQ Templates Management********** 
                        " WORK IN PROGRESS..."
                    </h2>
                    <p className={`${colors.text.body} text-lg max-w-2xl mx-auto`}>
                        Create and manage customized FAQ templates for different business sectors
                    </p>
                </div>

                {/* Alerts with source styling */}
                {(message || error) && (
                    <Container className="mb-8">
                        {message && (
                            <div className="p-4 bg-emerald-50 rounded-lg">
                                <p className="text-emerald-600">{message}</p>
                            </div>
                        )}
                        {error && (
                            <div className="p-4 bg-red-50 rounded-lg">
                                <p className="text-red-600">{error}</p>
                            </div>
                        )}
                    </Container>
                )}

                {/* Sectors Grid - matching source feature card style */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {sectors.map((sector, index) => (
                        <div key={sector._id} className="relative group">
                            {/* Gradient border effect - exact match to source */}
                            <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-indigo-500/20 
                                rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            
                            <div className="relative h-full bg-white rounded-xl p-6 shadow-lg hover:shadow-xl 
                                transition-all duration-300 border border-gray-100">
                                {/* Icon - matching source style */}
                                <div className={`p-3 rounded-lg bg-violet-500/10 w-fit mb-4`}>
                                    <ShieldCheck className={`w-6 h-6 text-violet-600`} />
                                </div>

                                {/* Content */}
                                <h3 className="text-xl font-semibold mb-3">{sector.name}</h3>
                                <p className={`${colors.text.body} mb-4`}>{sector.description}</p>

                                {/* Stats - matching source style */}
                                <div className="space-y-2 mb-6">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-2" />
                                        {`${templates.filter(t => t.sector === sector._id).length} Templates`}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-2" />
                                        {`Latest Version: ${Math.max(...templates
                                            .filter(t => t.sector === sector._id)
                                            .map(t => t.version || 0), 0)}`}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="space-y-3">
                                    <GradientButton
                                        onClick={() => handleViewTemplates(sector)}
                                        className="w-full"
                                    >
                                        <Eye className="w-4 h-4 mr-2" />
                                        View Templates
                                    </GradientButton>
                                    <button
                                        onClick={() => handleGenerateTemplate(sector)}
                                        disabled={generating}
                                        className="w-full px-4 py-2 rounded-xl border border-violet-200 
                                            bg-white hover:bg-violet-50 transition-colors flex items-center 
                                            justify-center text-violet-600 hover:text-violet-700"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Generate Template
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Templates Section - matching source style */}
                {selectedSector && !loading && (
                    <div className="mt-12">
                        <div className="text-center mb-8">
                            <h3 className={`${colors.text.heading} ${colors.primary.text} mb-4`}>
                                Templates for {selectedSector.name}
                            </h3>
                            <p className={`${colors.text.body} text-lg`}>
                                Select a template to edit or generate a new one
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {templates.map((template) => (
                                <div key={template._id} className="relative group">
                                    <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-indigo-500/20 
                                        rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    
                                    <div className="relative h-full bg-white rounded-xl p-6 shadow-lg hover:shadow-xl 
                                        transition-all duration-300 border border-gray-100">
                                        <div className={`p-3 rounded-lg bg-indigo-500/10 w-fit mb-4`}>
                                            <Settings className={`w-6 h-6 text-indigo-600`} />
                                        </div>

                                        <h4 className="text-xl font-semibold mb-3">
                                            Version {template.version}
                                        </h4>
                                        
                                        <div className="space-y-2 mb-6">
                                            <div className="flex items-center text-sm text-gray-600">
                                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mr-2" />
                                                {`Last updated: ${new Date(template.updatedAt).toLocaleDateString()}`}
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleEditTemplate(template)}
                                            className="w-full px-4 py-2 rounded-xl border border-violet-200 
                                                bg-white hover:bg-violet-50 transition-colors flex items-center 
                                                justify-center text-violet-600 hover:text-violet-700"
                                        >
                                            <Edit2 className="w-4 h-4 mr-2" />
                                            Edit Template
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* EnvChecker Component Integration */}
                <div className="mt-12">
                    <EnvChecker />
                </div>

                {/* Modal - matching source container style */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                        <Container className="w-full max-w-3xl bg-white p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className={`${colors.text.heading} text-xl`}>
                                    Edit FAQ Template - Version {currentTemplate.version}
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            
                            <textarea
                                value={currentTemplate.template}
                                onChange={handleTemplateChange}
                                rows={15}
                                className="w-full px-4 py-3 rounded-lg border border-gray-200 mb-6
                                    focus:border-violet-300 focus:ring focus:ring-violet-200 
                                    focus:ring-opacity-50"
                                placeholder="Edit the FAQ template..."
                            />

                            <div className="flex justify-end space-x-4">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-2 rounded-xl border border-gray-200 
                                        hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <GradientButton onClick={handleSaveTemplate}>
                                    Save Changes
                                </GradientButton>
                            </div>
                        </Container>
                    </div>
                )}
            </div>
        </section>
    );
};

export default AdminPage;
